<template>
  <div class="advisor-step">
    <div>
      <Heading :level="3" class="advisor-step__title">
        Qual a sua assessora ou assessor de investimentos?
      </Heading>
      <SearchBar
        v-model="searchValue"
        class="advisor-step__search-bar"
        data-testid="search-bar"
        :dropdown-items="[]"
        placeholder="Busque pelo nome"
        @on-clear="handleClear"
        @input="onInputChange"
      />
      <LoaderIndicator
        v-if="isFetchingList && !isScrolling"
        size="48"
        with-container
      />
      <HelperViewContextual
        v-else-if="advisorsList.length === 0 || !searchValue"
        class="advisor-step__helper"
        :title="helperData.title"
        :description="helperData.description"
        status="neutral"
        :icon="helperData.icon"
      />
      <div v-else class="advisor-step__list" data-testid="advisor-list">
        <Row
          v-for="(option, index) in advisorsList"
          :key="index"
          data-testid="advisor-option"
          has-interaction
          :has-line="index > 0"
          @click="selectOption(option)"
        >
          <template #top-left>
            <BaseText size="p4" :color="NebraskaColors.elementPrimary">
              {{ option.name }}
            </BaseText>
          </template>
          <template v-if="option.selected" #icon-right>
            <InitialAvatarIcon
              size="extra-small"
              class="advisor-step__list--icon"
              icon="EF0070"
            />
          </template>
        </Row>
        <LoaderIndicator
          v-if="isScrolling"
          with-container
          class="advisor-step__loader"
          size="24"
        />
      </div>
    </div>
    <div v-if="showButton" class="advisor-step__button-container">
      <ButtonPrimary
        class="advisor-step__button"
        label="Continuar"
        :loading="loading || isFetching"
        size="large"
        @click="submit"
      />
    </div>

    <Recaptcha ref="recaptcha" @token="onRecaptchaToken" />
  </div>
</template>
<script setup lang="ts">
import { debounce } from 'lodash-es';
import { computed, inject, onMounted, ref } from 'vue';

import {
  BaseText,
  ButtonPrimary,
  Heading,
  SearchBar,
  Row,
  InitialAvatarIcon,
  HelperViewContextual,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import { IAdvisorList } from '@/modules/onboarding/signup/types';
import { getAdvisorList } from '@/modules/onboarding/signup/services/api';
import {
  trackAdvisorModalView,
  trackFillAdvisorEmail,
  trackSendAdvisorEmail
} from '@/modules/onboarding/signup/helpers/track-events';
import { isBFFError } from '@/modules/common/helpers/bff';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import {
  IRecaptcha,
  Recaptcha,
  RecaptchaActions
} from '@/modules/authentication/components/Recaptcha';

defineProps({
  isFetching: { type: Boolean, default: false }
});

const searchValue = ref('');
const helperText = ref('');
const loading = ref<boolean>(false);
const selectedAdvisor = ref<IAdvisorList>();
const isFetchingList = ref(false);
const isScrolling = ref(false);
const isEmpty = ref(false);

const pagination = ref({
  pageSize: 15,
  pageIndex: 1,
  totalPages: 10
});

const recaptcha = ref<InstanceType<typeof Recaptcha> | null>(null);
const recaptchaToken = ref<string>('');

const advisorsList = ref<IAdvisorList[]>([]);

const emit = defineEmits(['close', 'submit']);

const updateUserHaveAdvisor = inject(
  'updateUserHaveAdvisor',
  (value: boolean) => ({
    value
  })
);
const updateAdvisor = inject('updateAdvisor', (value: IAdvisorList) => ({
  value
}));

const helperData = computed(() => {
  return searchValue.value && isEmpty.value
    ? {
        title: `Nenhum resultado encontrado para "${helperText.value}"`,
        description:
          'Verifique se há erros de digitação ou ortografia na sua pesquisa, por favor.',
        icon: 'EE0153'
      }
    : {
        title: 'Buscar assessora ou assessor',
        description:
          'Busque a sua assessora ou assessor de investimentos pelo nome.',
        icon: 'EE0151'
      };
});

const showButton = computed(() => {
  return (
    advisorsList.value.some(item => item.selected) && !isFetchingList.value
  );
});

const onInputChange = debounce(handleSearch, 500);

async function handleSearch(value: string) {
  helperText.value = value;

  await fetchAdvisorList();
  registerObserver();
}

function handleClear() {
  searchValue.value = '';
  helperText.value = '';

  advisorsList.value.splice(0, advisorsList.value.length);
  isEmpty.value = false;
}

function selectOption(value: {
  name: string;
  email: string;
  selected?: boolean;
}) {
  advisorsList.value = advisorsList.value.map(item => {
    return {
      ...item,
      selected: item.email === value.email
    };
  });

  trackFillAdvisorEmail();

  selectedAdvisor.value = value;
}

function submit() {
  loading.value = true;
  updateAdvisor(selectedAdvisor.value!);
  updateUserHaveAdvisor(true);
  emit('submit', selectedAdvisor.value!.email);
  trackSendAdvisorEmail();
}

async function onRecaptchaToken(token: string) {
  recaptchaToken.value = token;
}

async function handleRecaptcha() {
  const recaptchaEl = recaptcha.value as unknown as IRecaptcha;

  try {
    await recaptchaEl.onChallenge(RecaptchaActions.ADVISOR_LIST);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Ocorreu um erro ao continuar, verifique sua internet e tente novamente.',
      type: NotificationTypes.error
    });

    emit('close');
  }
}

async function fetchAdvisorList(
  fetch = true,
  scrolling = false,
  pageIndex = 1
) {
  try {
    isFetchingList.value = fetch;
    isScrolling.value = scrolling;

    if (!searchValue.value) {
      advisorsList.value = [];
      isEmpty.value = false;

      return;
    }

    const data = await getAdvisorList(
      recaptchaToken.value,
      searchValue.value,
      pageIndex
    );

    isEmpty.value = Boolean(
      data.advisorsResponse.length < 1 && searchValue.value
    );

    pagination.value = {
      pageIndex: data.pageIndex,
      pageSize: data.pageSize,
      totalPages: data.totalPages
    };

    const filteredList = data.advisorsResponse.map(advisor => {
      return {
        ...advisor,
        selected: false
      };
    });

    if (isScrolling.value) {
      advisorsList.value.push(...filteredList);
    } else {
      advisorsList.value = filteredList;
    }
  } catch (e) {
    let errorMessage =
      'Não foi possível realizar a operação, por favor tente novamente.';

    if (isBFFError(e)) {
      errorMessage = e.error.message.formatted;
    }

    notify({
      group: 'wrn-notifications',
      text: errorMessage,
      type: NotificationTypes.error
    });

    emit('close');
  } finally {
    isFetchingList.value = false;
    isScrolling.value = false;
  }
}

function observerLastRow(observerContentRow: IntersectionObserver) {
  const rows = document.querySelectorAll('[data-testid="advisor-option"]');

  if (rows.length === 0) return;
  if (pagination.value.pageIndex === pagination.value.totalPages) return;

  const lastRow = rows[rows.length - 1];
  observerContentRow.observe(lastRow);
}

function registerObserver() {
  const observerContentRow = new IntersectionObserver(
    async ([lastRow], observer) => {
      if (!lastRow.isIntersecting) return;

      observer.unobserve(lastRow.target);
      await fetchAdvisorList(false, true, pagination.value.pageIndex + 1);
      observerLastRow(observerContentRow);
    }
  );

  observerLastRow(observerContentRow);
}

onMounted(() => {
  trackAdvisorModalView();

  handleRecaptcha();
});
</script>
<style lang="less" scoped>
.advisor-step {
  padding: @size-spacing-x1100 0 @size-spacing-x500;
  height: 100vh;
  position: relative;

  @media screen and (max-width: 768px) {
    padding: 0;
    height: auto;
  }

  @media screen and (min-height: 1024px) and (max-width: 1023px) {
    padding: @size-spacing-x500 0;
    height: calc(100vh - 377px);
  }

  :deep(.selection__box) {
    border: none;
  }

  &__title {
    margin-bottom: @size-spacing-x500;
  }

  &__search-bar {
    margin-bottom: @size-spacing-x400;

    :deep(.button-icon) {
      color: @element-primary !important;
    }
  }

  &__helper {
    margin-top: @size-spacing-x1000;
  }

  &__list {
    @desktop-content: 264px;
    @mobile-content: 208px;

    max-height: calc(100vh - @desktop-content);
    box-sizing: border-box;
    overflow: scroll;

    @media screen and (max-width: 768px) {
      max-height: calc(100vh - @mobile-content);
    }

    @media screen and (min-height: 1024px) and (max-width: 1023px) {
      max-height: calc(100vh - 600px);
    }

    &--icon {
      background-color: transparent;
    }

    :deep(.wrapper) {
      height: 32px;
    }

    :deep(.row-item > div) {
      padding: @size-spacing-x500 @size-spacing-x400;
    }

    :deep(.container-left) {
      align-content: center;
    }

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__loader {
    min-height: 62px !important;
  }

  &__button-container {
    background-color: @background-secondary;
    position: absolute;
    bottom: 76px;
    width: 100%;

    @media screen and (max-width: 768px) {
      bottom: 0;
    }
  }

  &__button {
    width: 100%;
  }
}
</style>
